import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Collapse,
  Tabs,
  Tab,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Code as CodeIcon,
  School as SchoolIcon,
  Launch as LaunchIcon,
  Cloud as CloudIcon,
  Psychology as PsychologyIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Book as BookIcon,
  AccountBalance as AccountBalanceIcon,
  Gavel as GavelIcon,
  Campaign as CampaignIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
  Balance as BalanceIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import technologyListsData from '../data/technologyLists.json';
import nonTechListsData from '../data/nonTechLists.json';

interface MediumList {
  title: string;
  count: string;
  link: string;
  iconName: string;
  icon: React.ReactNode;
  category: string;
  description: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, mt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const MediumListsSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const [tabValue, setTabValue] = useState(0);

  const getIconComponent = (iconName: string): React.ReactNode => {
    const iconMap: { [key: string]: React.ReactNode } = {
      Code: <CodeIcon />, School: <SchoolIcon />, Cloud: <CloudIcon />, Psychology: <PsychologyIcon />,
      Business: <BusinessIcon />, TrendingUp: <TrendingUpIcon />, Book: <BookIcon />,
      AccountBalance: <AccountBalanceIcon />, Gavel: <GavelIcon />, Campaign: <CampaignIcon />,
      Assessment: <AssessmentIcon />, Group: <GroupIcon />, Balance: <BalanceIcon />,
    };
    return iconMap[iconName] || <CodeIcon />;
  };
  
  const technologyLists: MediumList[] = technologyListsData.technologyLists.map(item => ({ ...item, iconName: item.icon, icon: getIconComponent(item.icon) }));
  const nonTechLists: MediumList[] = nonTechListsData.nonTechLists.map(item => ({ ...item, iconName: item.icon, icon: getIconComponent(item.icon) }));

  const [activeList, setActiveList] = useState<{ [key: string]: MediumList | null }>({
    technology: isMobile ? null : technologyLists[0] || null,
    leadership: isMobile ? null : nonTechLists[0] || null,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (!isMobile) {
        if (newValue === 0) setActiveList(prev => ({...prev, leadership: isMobile ? null : prev.leadership, technology: technologyLists[0] || null}));
        if (newValue === 1) setActiveList(prev => ({...prev, technology: isMobile ? null : prev.technology, leadership: nonTechLists[0] || null}));
    } else {
        setActiveList({ technology: null, leadership: null });
    }
  };
  
  const handleSelectList = (section: 'technology' | 'leadership', list: MediumList) => {
    setActiveList(prev => {
      if (isMobile && prev[section]?.title === list.title) {
        return { ...prev, [section]: null };
      }
      return { ...prev, [section]: list };
    });
  };

  const renderTabPanel = (
    lists: MediumList[], 
    color: string,
    sectionKey: 'technology' | 'leadership'
  ) => {
    const currentActiveList = activeList[sectionKey];
    
    return (
        <Box sx={{ display: 'flex', flexDirection: {xs: 'column', md: 'row'}, p: {xs: 0, md: 2}, background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.6)', border: `1px solid ${color}20`, borderRadius: 4 }}>
          {isMobile ? (
            <Box sx={{ width: '100%' }}>
              {lists.map(list => {
                const isActive = currentActiveList?.title === list.title;
                return (
                  <Box key={list.title} sx={{ mb: 1, borderRadius: 2, overflow: 'hidden', border: `1px solid ${color}20`, background: isActive ? `${color}10` : 'transparent', transition: 'background .3s ease' }}>
                    <Box
                      onClick={() => handleSelectList(sectionKey, list)}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {list.icon}
                        <Typography 
                          sx={{ 
                            fontFamily: '"Roboto Mono", "Courier New", monospace', 
                            fontWeight: 600,
                            fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' }
                          }}
                        >
                          {list.title}
                        </Typography>
                      </Box>
                      <Box sx={{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>
                        <ExpandMoreIcon />
                      </Box>
                    </Box>
                    <Collapse in={isActive} timeout="auto" unmountOnExit>
                      <Box
                        component="a"
                        href={list.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'block', textDecoration: 'none', color: 'inherit',
                          p: 2, borderTop: `1px solid ${color}20`,
                          background: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.3)',
                          position: 'relative', transition: 'background 0.3s ease',
                          '&:hover': {
                            background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.5)',
                            '& .launch-icon-on-hover': { opacity: 1, transform: 'scale(1.1)' }
                          }
                        }}
                      >
                        <LaunchIcon
                          className="launch-icon-on-hover"
                        sx={{
                            position: 'absolute', top: 16, right: 16, color: color,
                            opacity: 0, transition: 'all 0.3s ease'
                          }}
                        />
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: { xs: 1.5, md: 2 }, 
                            lineHeight: { xs: 1.5, md: 1.6 }, 
                            fontFamily: '"Roboto Mono", "Courier New", monospace',
                            fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' }
                          }}
                        >
                          {list.description}
                        </Typography>
                        <Chip label={`${list.category} · ${list.count}`} size="small" sx={{ backgroundColor: `${color}20`, color: color, fontWeight: 500 }}/>
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <>
              <Box sx={{ width: {xs: '100%', md: '35%'}, borderRight: {xs: 'none', md: `1px solid ${color}20`}, display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
                {lists.map(list => (
                  <Button
                    key={list.title}
                    onClick={() => handleSelectList(sectionKey, list)}
                    startIcon={list.icon}
                    sx={{
                      justifyContent: 'flex-start',
                      p: 1.5,
                      borderRadius: 2,
                      fontWeight: currentActiveList?.title === list.title ? 700 : 500,
                      backgroundColor: currentActiveList?.title === list.title 
                        ? (isDark ? 'rgba(0, 0, 0, 0.4)' : `${color}20`)
                        : 'transparent',
                      color: currentActiveList?.title === list.title 
                        ? (isDark ? '#ffffff' : color)
                        : theme.palette.text.primary,
                      border: currentActiveList?.title === list.title && isDark
                        ? '1px solid rgba(0, 0, 0, 0.5)'
                        : 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        backgroundColor: isDark 
                          ? (currentActiveList?.title === list.title ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)')
                          : `${color}15` 
                      },
                      fontFamily: '"Roboto Mono", "Courier New", monospace',
                      textTransform: 'none',
                    }}
                  >
                    {list.title}
                  </Button>
                ))}
              </Box>

              <Box sx={{ flex: 1, p: {xs: 1, md: 3}, mt: {xs: 2, md: 0} }}>
                <AnimatePresence mode="wait">
                  {currentActiveList && (
                    <motion.div
                      key={currentActiveList.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <Box
                        component="a"
                        href={currentActiveList.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'block', textDecoration: 'none', color: 'inherit',
                          p: 3, borderRadius: 3, position: 'relative',
                          background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: `0 8px 25px ${color}30`,
                            '& .launch-icon-on-hover': { opacity: 1, transform: 'scale(1.1)' }
                          }
                        }}
                      >
                        <LaunchIcon
                          className="launch-icon-on-hover"
                          sx={{
                            position: 'absolute', top: 24, right: 24, color: color,
                            opacity: 0, transition: 'all 0.3s ease'
                          }}
                        />
                        <Typography 
                          variant="h5" 
                          component="h3" 
                          sx={{ 
                            fontWeight: 600, 
                            color: theme.palette.text.primary, 
                            mb: { xs: 0.75, md: 1 }, 
                            fontFamily: '"Roboto Mono", "Courier New", monospace',
                            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' }
                          }}
                        >
                          {currentActiveList.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: { xs: 1.5, md: 2 }, 
                            lineHeight: { xs: 1.5, md: 1.6 }, 
                            fontFamily: '"Roboto Mono", "Courier New", monospace',
                            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' }
                          }}
                        >
                          {currentActiveList.description}
                        </Typography>
                        <Chip label={`${currentActiveList.category} · ${currentActiveList.count}`} size="small" sx={{ backgroundColor: `${color}20`, color: color, fontWeight: 500 }}/>
                      </Box>
    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </>
          )}
        </Box>
    );
  };

  return (
    <Box id="learnings" sx={{ py: { xs: 4, sm: 6, md: 8 }, backgroundColor: theme.palette.background.default, minHeight: { xs: 'auto', md: '100vh' }, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '100%', background: isDark ? 'linear-gradient(135deg, rgba(26, 115, 232, 0.02) 0%, rgba(52, 168, 83, 0.02) 100%)' : 'linear-gradient(135deg, rgba(26, 115, 232, 0.01) 0%, rgba(52, 168, 83, 0.01) 100%)', zIndex: 0, }, }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <Box textAlign="center" mb={{ xs: 4, sm: 6, md: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              className="section-heading-allura"
              sx={{
                fontFamily: 'Allura, cursive !important', 
                fontWeight: 400, 
                fontSize: { xs: '36px !important', sm: '42px !important', md: '48px !important' },
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #EC4899 100%)'
                  : 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 50%, #DB2777 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'inherit',
                mb: 2,
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '48px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '56px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '68px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '72px !important',
                },
              }}
            >
              Learnings
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              className="section-description-allura"
              sx={{ 
                fontFamily: 'Allura, cursive !important', 
                fontWeight: 400,
                maxWidth: 700, 
                mx: 'auto',
                fontSize: '28px !important',
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '32px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '34px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '35px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '36px !important',
                },
              }}
            >
              Explore my carefully curated collections of articles on technology, leadership, and business
            </Typography>
          </Box>
        </motion.div>

        <Box sx={{ width: '100%', typography: 'body1' }}>
            <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="learnings tabs"
                centered
                sx={{
                    mb: 4,
                    '& .MuiTabs-indicator': {
                        height: 4,
                        borderRadius: 4,
                        backgroundColor: tabValue === 0 ? '#1976d2' : '#388e3c',
                        transition: 'all 0.3s ease',
                    },
                }}
            >
                <Tab 
                    icon={<CodeIcon sx={{ color: tabValue === 0 ? '#1976d2' : 'inherit' }} />} 
                    iconPosition="start" 
                    label="Technology Nuggets"
                    sx={{
                        fontWeight: tabValue === 0 ? 700 : 500,
                        transition: 'all 0.3s ease',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        px: { xs: 1, sm: 2 },
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #EC4899 100%)'
                          : 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 50%, #DB2777 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        color: 'transparent',
                    }}
                />
                <Tab 
                    icon={<SchoolIcon sx={{ color: tabValue === 1 ? '#388e3c' : 'inherit' }} />} 
                    iconPosition="start" 
                    label="Leadership Gems"
                    sx={{
                        fontWeight: tabValue === 1 ? 700 : 500,
                        transition: 'all 0.3s ease',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        px: { xs: 1, sm: 2 },
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #EC4899 100%)'
                          : 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 50%, #DB2777 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        color: 'transparent',
                    }}
                />
            </Tabs>
            <TabPanel value={tabValue} index={0}>
                {renderTabPanel(technologyLists, isDark ? '#000000' : '#1976d2', 'technology')}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
                {renderTabPanel(nonTechLists, isDark ? '#000000' : '#388e3c', 'leadership')}
            </TabPanel>
        </Box>
      </Container>
    </Box>
  );
};

export default MediumListsSection; 